import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { loadFromSupabase } from "../supabaseDB";
import { loadCompanyProfile } from "./CompanyProfile";
import { isFourSeasonsClientName, filterContactsByCategory, appendDisclosureByAccount } from "./emailHelpers";
import { buildSlPassingIndex } from "./saleStatusCalc";
import EmailDialog from "./EmailDialog";
import SearchSelect from "./SearchSelect";
import {
  defaultOpenSeasonIds,
  buildSeasonFilterOptions,
  seasonFilterPlaceholder,
  resolveSeasonId,
} from "./seasonFilter";
import {
  REPORT_COLUMNS,
  STATUS_COLORS,
  buildShipmentRows,
  buildBillRows,
  buildExpenseBillRows,
  buildInvoiceRows,
  buildReceivedVsSoldRows,
  buildAgingRows,
  buildShipmentStatusTotals,
  buildShipmentPeriodStatusChart,
  groupRows,
  labelRowsByPeriod,
  computeGrandTotal,
  insertDetailSubtotals,
  insertEntitySubtotals,
  insertEntityHeaderRows,
} from "./reportGeneratorCalc";
import { exportReportXlsx, buildReportPdfBlob } from "./reportGeneratorExport";

/* ============================================================
   Report Generator — Xero-style: pick a report type, an entity
   (supplier or client), a period, detail level, status, which
   columns to include, and how to group rows, then generate one
   compact Excel file (no logo, no big titles — see
   reportGeneratorExport.js).

   Arturo, 2026-09-17, after rejecting a first "fixed reports"
   attempt: "yo selecciono que quiero incluir, producto, cajas,
   deducciones a detalle, o globales, rango de fechas, por mes, por
   semanas, por temporada, facturas liquidadas, pendientes de
   pagar... ve como los genera xero y usa ese modo".
   ============================================================ */

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }

const T = {
  en: {
    title: "Report Generator", subtitle: "Pick a report type, an entity, a period, and generate a compact Excel file.",
    reportType: "Report Type",
    typeShipments: "Shipments (products received)",
    typeBills: "Shipment Bills (paid to shipper)",
    typeExpenseBills: "Expense Bills (service costs)",
    typeInvoices: "Invoices (client sales)",
    typeReceivedVsSold: "Received vs Sold (profit)",
    typeAging: "Payables & Receivables Aging",
    entityType: "Entity", entitySupplier: "Shipper", entityClient: "Client",
    entity: "Select", allEntities: "All",
    period: "Period", from: "From", to: "To",
    groupBy: "Group By", groupNone: "None (line by line)", groupWeek: "Week", groupMonth: "Month", groupSeason: "Season",
    status: "Status", statusAll: "All", statusPaid: "Paid", statusPending: "Awaiting", statusDraft: "Draft", statusOverdue: "Overdue",
    detailLevel: "Detail Level", detailDetailed: "Detailed", detailGlobal: "Global (summary)",
    columns: "Columns to Include",
    generate: "Generate Excel", generatePdf: "Generate PDF", generatingPdf: "Generating…", previewPdf: "Preview PDF",
    pdfPreviewTitle: "PDF Preview", download: "Download", close: "Close",
    execute: "Execute", rowsReady: "rows match these filters",
    noRows: "No rows match these filters.",
    noDataYet: "Loading data…",
    stale: "Filters changed — press Execute again to refresh the table before exporting.",
    notExecutedYet: "Press Execute to preview the report before exporting.",
    previewTitle: "Report Preview",
    grandTotalLabel: "GRAND TOTAL",
    includeRow: "Include this row in the totals and export",
    sortColumn: "Click to sort by this column",
    pdfOrientation: "PDF Orientation", orientationAuto: "Auto", orientationPortrait: "Portrait", orientationLandscape: "Landscape",
    /* Send-to-shipper (2026-09-23, Arturo: Shipments report — "poder
       enviar ese reporte via email al shipper") */
    sendToShipper: "Send to Shipper",
    sendReportTitle: "Send Report",
    sendReportBody: "Greetings,\n\nAttached please find the shipment report covering the period and shipments referenced, for your review.\n\nPlease review the enclosed document and let us know if you have any questions.",
    /* Status breakdown (2026-09-23, Arturo: "totales de los distntos
       status tanto cajas como pallets con porcentaje de acuerdo al
       total recibido... y por porducto") */
    statusBreakdownTitle: "Totals by Status",
    statusBreakdownAllProducts: "All Products",
    statusBreakdownCases: "Cases", statusBreakdownPallets: "Pallets",
    statusBreakdownTotalReceived: "Total Received",
    chartPeriod: "Chart Period", chartPeriodWeek: "Week", chartPeriodMonth: "Month", chartPeriodSeason: "Season",
    includeChart: "Include chart in PDF",
  },
  es: {
    title: "Generador de Reportes", subtitle: "Elige un tipo de reporte, una entidad, un periodo, y genera un Excel compacto.",
    reportType: "Tipo de Reporte",
    typeShipments: "Embarques (productos recibidos)",
    typeBills: "Shipment Bills (pagado al shipper)",
    typeExpenseBills: "Expense Bills (gastos de servicio)",
    typeInvoices: "Invoices (ventas a cliente)",
    typeReceivedVsSold: "Recibido vs Vendido (ganancia)",
    typeAging: "Antigüedad de Saldos (Por Pagar y Por Cobrar)",
    entityType: "Entidad", entitySupplier: "Shipper", entityClient: "Cliente",
    entity: "Selecciona", allEntities: "Todos",
    period: "Periodo", from: "Desde", to: "Hasta",
    groupBy: "Agrupar Por", groupNone: "Ninguno (línea por línea)", groupWeek: "Semana", groupMonth: "Mes", groupSeason: "Temporada",
    status: "Estado", statusAll: "Todos", statusPaid: "Liquidadas", statusPending: "Pendientes", statusDraft: "Borrador", statusOverdue: "Vencidas",
    detailLevel: "Nivel de Detalle", detailDetailed: "Detallado", detailGlobal: "Global (resumen)",
    columns: "Columnas a Incluir",
    generate: "Generar Excel", generatePdf: "Generar PDF", generatingPdf: "Generando…", previewPdf: "Vista Previa PDF",
    pdfPreviewTitle: "Vista Previa del PDF", download: "Descargar", close: "Cerrar",
    execute: "Ejecutar", rowsReady: "filas coinciden con estos filtros",
    noRows: "Ninguna fila coincide con estos filtros.",
    noDataYet: "Cargando datos…",
    stale: "Los filtros cambiaron — vuelve a dar Ejecutar para actualizar la tabla antes de exportar.",
    notExecutedYet: "Da click en Ejecutar para previsualizar el reporte antes de exportar.",
    previewTitle: "Vista Previa del Reporte",
    grandTotalLabel: "TOTAL GENERAL",
    includeRow: "Incluir este renglón en los totales y la exportación",
    sortColumn: "Click para ordenar por esta columna",
    pdfOrientation: "Orientación del PDF", orientationAuto: "Automática", orientationPortrait: "Vertical", orientationLandscape: "Horizontal",
    sendToShipper: "Enviar al Shipper",
    sendReportTitle: "Enviar Reporte",
    sendReportBody: "Saludos,\n\nAdjunto encontrará el reporte de embarques que cubre el periodo y los lotes referenciados, para su revisión.\n\nPor favor revise el documento adjunto y avísenos si tiene alguna pregunta.",
    statusBreakdownTitle: "Totales por Status",
    statusBreakdownAllProducts: "Todos los Productos",
    statusBreakdownCases: "Cajas", statusBreakdownPallets: "Pallets",
    statusBreakdownTotalReceived: "Total Recibido",
    chartPeriod: "Periodo del Gráfico", chartPeriodWeek: "Semana", chartPeriodMonth: "Mes", chartPeriodSeason: "Temporada",
    includeChart: "Incluir gráfico en el PDF",
  },
};

const REPORT_TYPE_OPTIONS = [
  { value: "shipments", labelKey: "typeShipments", entity: "supplier" },
  { value: "bills", labelKey: "typeBills", entity: "supplier" },
  { value: "expenseBills", labelKey: "typeExpenseBills", entity: "vendor" },
  { value: "invoices", labelKey: "typeInvoices", entity: "client" },
  { value: "receivedVsSold", labelKey: "typeReceivedVsSold", entity: "supplier" },
  { value: "aging", labelKey: "typeAging", entity: "either" },
];

/* Per-record identifier columns — dropped from grouped (week/month/
   season) views, since a folded-together period row can't meaningfully
   carry a single lot/PU/PO/invoice/date/status. See exportColumns. */
var RECORD_IDENTIFIER_COLS = ["lot", "puNumber", "poNumber", "shipperInvoice", "proforma", "date", "dueDate", "lineType", "description", "status"];

/* Arturo, 2026-09-17: "no es necesario tener una columna de cliente o
   shipper, se puede agregar un renglon por cada cliente arriba de sus
   propios lotes... o sea se segmentan por cliente o shipper o
   proveedor de servicio" — which row field identifies "the entity" for
   each report type, so its rows can be sectioned with a header instead
   of repeating that column on every line. */
var ENTITY_FIELD_BY_REPORT = {
  shipments: "supplierName",
  bills: "supplierName",
  expenseBills: "vendor",
  invoices: "clientName",
  receivedVsSold: "supplierName",
  aging: "entityName",
};

var inp = { padding: "7px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
var fieldLabel = { fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "block" };

function Field({ label, children, width }) {
  return <div style={{ minWidth: width || 160 }}><span style={fieldLabel}>{label}</span>{children}</div>;
}

/* Accounting-style money formatting for the on-screen preview table —
   negatives (deduction lines: Fee/Commission/Advance Paid) render as
   "($832.36)" instead of the misleading "$-832.36", same convention
   already used by reportGeneratorExport.js's PDF writer. */
function fmtMoneyCell(v) {
  var n = Number(v);
  if (isNaN(n)) return "";
  var neg = n < 0;
  var s = "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? "(" + s + ")" : s;
}

/* Generic pre-builder filter — applies to raw source records of any
   report type before its row-builder runs. `getDate` is an accessor
   function (records use different date field names per type). */
function applyCommonFilters(records, getDate, opts) {
  return (records || []).filter(function (r) {
    var date = getDate(r) || "";
    if (opts.dateFrom && date && date < opts.dateFrom) return false;
    if (opts.dateTo && date && date > opts.dateTo) return false;
    if (opts.entityField && opts.entityValue && String(r[opts.entityField] || "") !== String(opts.entityValue)) return false;
    if (opts.filterSeasons && opts.filterSeasons.length > 0) {
      var sid = resolveSeasonId(opts.seasons, date, r.seasonId);
      if (!sid || opts.filterSeasons.indexOf(String(sid)) < 0) return false;
    }
    return true;
  });
}

export default function ReportGenerator({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];

  var [loaded, setLoaded] = useState(false);
  var [shipments, setShipments] = useState([]);
  var [bills, setBills] = useState([]);
  var [expenseBills, setExpenseBills] = useState([]);
  var [invoices, setInvoices] = useState([]);
  var [suppliers, setSuppliers] = useState([]);
  var [clients, setClients] = useState([]);
  var seasons = useMemo(function () { return loadLS("gfi-seasons", []); }, [loaded]);

  var [companyName, setCompanyName] = useState("");
  var [slInvoices, setSlInvoices] = useState([]);
  useEffect(function () {
    var alive = true;
    Promise.all([
      loadFromSupabase("shipments"),
      loadFromSupabase("bills"),
      loadFromSupabase("expense_bills"),
      loadFromSupabase("invoices"),
      loadFromSupabase("suppliers"),
      loadFromSupabase("clients"),
      loadFromSupabase("seasons").then(function (rows) { try { localStorage.setItem("gfi-seasons", JSON.stringify(rows)); } catch (e) {} }),
      /* Arturo, 2026-09-17: the Aging report's Xero-style title block
         needs the real company legal name — loadCompanyProfile() only
         reads a synchronous in-memory cache that may not be primed yet
         in this tab (same stale-cache class of bug documented
         elsewhere in this app), so force a fresh load here first. */
      loadFromSupabase("company_profile"),
      /* Arturo, 2026-09-17: "los de four seasons deben mostrar el
         numero propio de invoice que lleva sweetleaf" — need the
         SweetLeaf mirror invoices (each carries gfiPuNumber + its own
         invoiceNumber) to resolve that for the Aging receivables
         report. */
      loadFromSupabase("sweetleaf_invoices"),
    ]).then(function (results) {
      if (!alive) return;
      setShipments(results[0] || []); setBills(results[1] || []); setExpenseBills(results[2] || []);
      setInvoices(results[3] || []); setSuppliers(results[4] || []); setClients(results[5] || []);
      setSlInvoices(results[8] || []);
      try { setCompanyName(loadCompanyProfile().legalName || ""); } catch (e) {}
      setLoaded(true);
    }).catch(function () { if (alive) setLoaded(true); });
    return function () { alive = false; };
  }, []);

  var [reportType, setReportType] = useState("shipments");
  var typeDef = REPORT_TYPE_OPTIONS.find(function (o) { return o.value === reportType; });
  var colDef = REPORT_COLUMNS[reportType];

  var [entityType, setEntityType] = useState("supplier");
  useEffect(function () { if (typeDef.entity !== "either") setEntityType(typeDef.entity); }, [reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  var [entityValue, setEntityValue] = useState("");
  useEffect(function () { setEntityValue(""); }, [reportType, entityType]);

  var [dateFrom, setDateFrom] = useState("");
  var [dateTo, setDateTo] = useState("");
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [groupBy, setGroupBy] = useState("none");
  var [statusFilter, setStatusFilter] = useState("");
  var [detailLevel, setDetailLevel] = useState("detail");
  /* Arturo, 2026-09-17: "los pdf deben estar en orientacion portrait no
     landscape porque ahora se ven muy angostos, solo si el reporte
     tuviera demasiadas columnas entonces cambiar orientacion o en su
     defecto permitirme a mi... seleccionar la orientacion optima" —
     "auto" (reportGeneratorExport.js's resolveOrientation) picks
     Portrait unless there are enough columns to need Landscape, but
     2026-10-07, Arturo: "la orientacion del pdf por default debe ser
     portrait" — the selector itself now starts on the explicit
     Portrait option, not Auto, so a report never silently switches to
     Landscape on its own; Arturo can still pick Auto or Landscape by
     hand when a wide report genuinely needs it. */
  var [pdfOrientation, setPdfOrientation] = useState("portrait");
  /* Weekly/monthly/seasonal status chart controls (2026-09-23, Arturo:
     "que me de la opcion de seleccionar si la grafica la quiero por
     semana o por mes o gasta por temporada si quiero cpomparar varias
     temporadas" + "incluso que me de la opcion si quiero incluir
     grafica o no"). Both Shipments-report-only; chart defaults ON
     (week) since it's the feature Arturo just asked to be built, easy
     to uncheck per report run. */
  var [chartPeriodBy, setChartPeriodBy] = useState("week");
  var [includeChart, setIncludeChart] = useState(true);

  var activeColumns = reportType === "aging"
    ? (entityType === "client" ? colDef.columnsReceivables : colDef.columnsPayables)
    : colDef.hasDetailToggle ? colDef[detailLevel === "detail" ? "detail" : "global"] : colDef.columns;

  var [checkedCols, setCheckedCols] = useState(function () {
    var m = {}; activeColumns.forEach(function (c) { m[c.key] = true; }); return m;
  });
  useEffect(function () {
    var m = {}; activeColumns.forEach(function (c) { m[c.key] = true; }); setCheckedCols(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, detailLevel, entityType]);

  var activeSuppliers = useMemo(function () { return suppliers.filter(function (s) { return s.isActive !== false; }); }, [suppliers]);
  var activeClients = useMemo(function () { return clients.filter(function (c) { return c.isActive !== false; }); }, [clients]);
  var uniqueVendors = useMemo(function () { return [...new Set(expenseBills.map(function (e) { return e.vendor; }).filter(Boolean))].sort(); }, [expenseBills]);

  /* Arturo, 2026-09-17: "nombre completo no usar nombre corto del
     cliente/shipper" — Bill.shipperName/Invoice.clientName/etc. store
     the SHORT name (this app's own convention — see CLAUDE.md's "Short
     Name vs Full Name"), but the entity section header is a document-
     style heading, so it resolves to the real legal/full name from the
     Supplier/Client catalog wherever possible. By id (aging, which
     carries supplierId/clientId) and by short-name lookup (every other
     report type, whose rows only carry the plain name string). */
  var suppliersById = useMemo(function () { var m = {}; suppliers.forEach(function (s) { if (s.id) m[s.id] = s.fullName || s.shortName || ""; }); return m; }, [suppliers]);
  var clientsById = useMemo(function () { var m = {}; clients.forEach(function (c) { if (c.id) m[c.id] = c.fullName || c.shortName || ""; }); return m; }, [clients]);
  var supplierNameMap = useMemo(function () { var m = {}; suppliers.forEach(function (s) { if (s.shortName) m[s.shortName] = s.fullName || s.shortName; }); return m; }, [suppliers]);
  var clientNameMap = useMemo(function () { var m = {}; clients.forEach(function (c) { if (c.shortName) m[c.shortName] = c.fullName || c.shortName; }); return m; }, [clients]);
  /* Arturo, 2026-09-17: "en Sweetleaf PU = GFI Invoice, invoice = SL
     invoice number" — {gfiPuNumber: slInvoiceNumber}, only ever
     populated for invoices actually copied to SweetLeaf (Four
     Seasons); buildAgingRows falls back to the plain GFI PU# for
     every other client. */
  var slInvoiceByPu = useMemo(function () {
    var m = {};
    slInvoices.forEach(function (s) { if (s.gfiPuNumber && s.invoiceNumber) m[s.gfiPuNumber] = s.invoiceNumber; });
    return m;
  }, [slInvoices]);

  var entityOptions = useMemo(function () {
    if (typeDef.entity === "vendor") return uniqueVendors.map(function (v) { return { value: v, label: v }; });
    if (entityType === "client") return activeClients.map(function (c) { return { value: c.id, label: c.shortName || c.fullName }; });
    return activeSuppliers.map(function (s) { return { value: s.id, label: s.shortName || s.fullName }; });
  }, [typeDef, entityType, activeSuppliers, activeClients, uniqueVendors]);

  /* Live preview count — recomputed with every filter change so
     Arturo sees "N rows will be exported" before committing to a
     download, per the plan's lightweight-feedback note. */
  /* Per-lot Status column (Shipments report) — same live-computed
     source ShipmentRegistry.js's own SALE column reads, never a
     stored value (REGLA DE STATUS DE PALLET). Built once here so
     buildShipmentRows doesn't have to. */
  var slPassingIndex = useMemo(function () { return buildSlPassingIndex(slInvoices); }, [slInvoices]);

  var previewRows = useMemo(function () {
    if (!loaded) return [];
    var commonOpts = { dateFrom: dateFrom, dateTo: dateTo, seasons: seasons, filterSeasons: filterSeasons };
    var rows = [];
    if (reportType === "shipments") {
      var shp = applyCommonFilters(shipments, function (s) { return s.dateArrival || s.dateDeparture; }, { ...commonOpts, entityField: "supplierId", entityValue: entityValue });
      var shpLots = shp.map(function (s) { return s.lot; });
      var scopedBills = bills.filter(function (b) { return shpLots.indexOf(b.lot) >= 0; });
      rows = buildShipmentRows(shp, scopedBills, expenseBills, { detail: detailLevel === "detail", invoices: invoices, slPassingIndex: slPassingIndex, lang: lang });
    } else if (reportType === "bills") {
      var b2 = applyCommonFilters(bills, function (b) { return b.billDate; }, { ...commonOpts, entityField: "supplierId", entityValue: entityValue });
      rows = buildBillRows(b2, { detail: detailLevel === "detail" });
    } else if (reportType === "expenseBills") {
      var eb = applyCommonFilters(expenseBills, function (e) { return e.date; }, { ...commonOpts, entityField: "vendor", entityValue: entityValue });
      rows = buildExpenseBillRows(eb, { detail: detailLevel === "detail" });
    } else if (reportType === "invoices") {
      var inv = applyCommonFilters(invoices, function (i) { return i.date; }, { ...commonOpts, entityField: "clientId", entityValue: entityValue });
      rows = buildInvoiceRows(inv, { detail: detailLevel === "detail" });
    } else if (reportType === "receivedVsSold") {
      /* Arturo, 2026-09-17: "en realidad en el reporte received vs
         sold profit, entity no deberia existir solo shippers y ahi
         filtrar" — this report is inherently shipper-scoped (it's
         "what GFI received FROM a shipper vs what it sold"), so the
         Entity Type toggle (Shipper/Client) was removed entirely —
         REPORT_TYPE_OPTIONS now pins entity:"supplier" for this type,
         same as Shipments/Bills. */
      var scopedShipments = applyCommonFilters(shipments, function (s) { return s.dateArrival || s.dateDeparture; }, { ...commonOpts, entityField: "supplierId", entityValue: entityValue });
      var lots = [...new Set(scopedShipments.map(function (s) { return s.lot; }).filter(Boolean))];
      var billsByLot = {}; bills.forEach(function (b) { if (!b.lot) return; (billsByLot[b.lot] || (billsByLot[b.lot] = [])).push(b); });
      var shipmentsByLot = {}; shipments.forEach(function (s) { if (s.lot) shipmentsByLot[s.lot] = s; });
      rows = buildReceivedVsSoldRows(lots, billsByLot, shipmentsByLot);
    } else if (reportType === "aging") {
      if (entityType === "client") {
        var agingInv = applyCommonFilters(invoices, function (i) { return i.date; }, { ...commonOpts, entityField: "clientId", entityValue: entityValue });
        rows = buildAgingRows(agingInv, { kind: "receivables", clientsById: clientsById, slInvoiceByPu: slInvoiceByPu });
      } else {
        var agingBills = applyCommonFilters(bills, function (b) { return b.billDate; }, { ...commonOpts, entityField: "supplierId", entityValue: entityValue });
        rows = buildAgingRows(agingBills, { kind: "payables", suppliersById: suppliersById });
      }
    }
    /* "Awaiting" is one concept with two different underlying status
       strings depending on report type (Expense Bills use "awaiting",
       Bills/Invoices use "pending") — 2026-10-07, Arturo: "el filtro
       muestra 4 veces pending y no muestra awaiting... solo waiting"
       — unify both into a single filter option instead of listing
       the same label 4 times pointing at different values. */
    if (statusFilter && colDef.hasStatusFilter) {
      rows = rows.filter(function (r) {
        if (statusFilter === "awaiting") return r.status === "awaiting" || r.status === "pending";
        return r.status === statusFilter;
      });
    }
    /* Generic full-name resolution for every OTHER report type's
       entity field (aging already resolved by id, above, more
       precisely) — see suppliersById/clientsById/supplierNameMap/
       clientNameMap comment further up. */
    if (reportType !== "aging") {
      var efRaw = ENTITY_FIELD_BY_REPORT[reportType];
      if (efRaw) {
        var nameMap = efRaw === "clientName" ? clientNameMap : efRaw === "vendor" ? {} : supplierNameMap;
        rows = rows.map(function (r) {
          if (!(efRaw in r) || !r[efRaw]) return r;
          var full = nameMap[r[efRaw]];
          return full && full !== r[efRaw] ? { ...r, [efRaw]: full } : r;
        });
      }
    }
    /* Arturo, 2026-09-17: "[lot/PO/date] deberian desaparecer solo si
       detail level es global (summary) porque si es detailed entonces
       si hay forma de detallar cada embarque" — Detailed mode keeps
       every individual record row (never collapses into one row per
       period) so Lot/PO#/Date stay meaningful; only Global/summary
       mode (or report types with no detail toggle at all, e.g.
       Received vs Sold) actually merges rows via groupRows. Aging
       rows are already one row per open record with no grouping UI at
       all (see the Field below), so it's excluded here too. */
    var isDetailedRecords = detailLevel === "detail" && colDef.hasDetailToggle;
    /* General rule (Arturo, 2026-09-17): reports sort ascending by
       default — chronological (oldest first) when ungrouped, by the
       group's period key when Group By is set (ISO week/month keys
       and season labels both sort correctly as plain strings).
       Arturo, same day: "se segmentan por cliente o shipper o
       proveedor de servicio" — the entity (Shipper/Client/Vendor) now
       sorts FIRST, so every entity's rows land contiguous and can be
       wrapped in a section header below; date/period stays as the
       secondary, within-entity sort. Resolved before groupRows below
       so period grouping can be scoped per entity too (2026-10-07,
       see groupRows' own comment). */
    var entityField = ENTITY_FIELD_BY_REPORT[reportType];
    if (reportType !== "aging") {
      rows = isDetailedRecords ? labelRowsByPeriod(rows, groupBy, lang) : groupRows(rows, groupBy, activeColumns, lang, entityField);
    }
    /* Arturo, 2026-09-23: "necesito se ordenen en el pdf y la
       previsualizacion, por lote de manera ascendente" — for the
       Shipments report specifically, Lot (numeric-aware, so "58050"
       sorts after "58049" and not alphabetically) replaces Date as the
       within-entity/within-period sort key. Every other report type
       keeps the 2026-09-17 date-based rule untouched. */
    rows = rows.slice().sort(function (a, b) {
      if (entityField) {
        var ea = a[entityField] || "", eb = b[entityField] || "";
        if (ea !== eb) return ea < eb ? -1 : 1;
      }
      if (reportType === "shipments") {
        var pa = groupBy !== "none" ? (a.period || "") : "";
        var pb = groupBy !== "none" ? (b.period || "") : "";
        if (pa !== pb) return pa < pb ? -1 : 1;
        var la = String(a.lot || ""), lb = String(b.lot || "");
        if (la !== lb) return la.localeCompare(lb, undefined, { numeric: true, sensitivity: "base" });
        return 0;
      }
      var ka = groupBy !== "none" ? (a.period || "") : (a._sortKey || a.date || "");
      var kb = groupBy !== "none" ? (b.period || "") : (b._sortKey || b.date || "");
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
    /* Arturo, 2026-09-17: a Detailed report must subtotal each lot/
       bill/invoice's line group before moving to the next record's
       lines. Now runs whether or not Group By is set — Detailed mode
       never collapses rows anymore, so there's always still a real
       per-lot line group to subtotal, period grouping or not. */
    if (isDetailedRecords) {
      rows = insertDetailSubtotals(rows, activeColumns, lang);
    }
    /* Arturo, 2026-09-17: "no es necesario tener una columna de cliente
       o shipper, se puede agregar un renglon por cada cliente arriba
       de sus propios lotes... titulos de columnas y despues el
       detalle" — wrap each entity's now-contiguous rows with a section
       header; exportColumns (below) drops the entity-name column
       since the header conveys it instead.
       2026-10-07, Arturo: "no me da totales por cliente, proveedor...
       necesito subtotales por vendor" — sum each entity's section
       before sectioning it off with the next entity's header. */
    if (entityField) {
      rows = insertEntitySubtotals(rows, entityField, activeColumns, lang);
      rows = insertEntityHeaderRows(rows, entityField);
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, reportType, entityType, entityValue, dateFrom, dateTo, filterSeasons, seasons, groupBy, statusFilter, detailLevel, shipments, bills, expenseBills, invoices, suppliersById, clientsById, supplierNameMap, clientNameMap, slInvoiceByPu]);

  /* All season ids -> display name, open and closed alike (not just
     the ones currently checked in the Season filter) — shared by
     seasonNamesLabel (near periodLabel below) and by the weekly/
     monthly/season chart's "season" grouping, which needs to label
     ANY season a shipment might resolve to. Declared here (before any
     useMemo that needs it) rather than near periodLabel where it used
     to live, so it's actually initialized by the time the chart memo
     below reads it. */
  var seasonNameById = useMemo(function () {
    var map = {};
    buildSeasonFilterOptions(lang).forEach(function (g) { (g.options || []).forEach(function (o) { map[o.value] = o.label; }); });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, seasons]);

  /* Status breakdown (2026-09-23, Arturo: "se puede agregar a este
     mismo reporte totales de los distntos status tanto cajas como
     pallets con porcentaje de acuerdo al total recibido? ... y por
     porducto"). Computed independently from previewRows — it needs the
     shipments' own per-line cases/pallets regardless of Detail
     Level/Group By, which previewRows may have already collapsed or
     expanded into fee/expense lines. Same date/season/entity filters
     as the main table, applied fresh here. Only meaningful for the
     Shipments report type. */
  var shipmentStatusTotals = useMemo(function () {
    if (!loaded || reportType !== "shipments") return null;
    var commonOpts = { dateFrom: dateFrom, dateTo: dateTo, seasons: seasons, filterSeasons: filterSeasons };
    var shp = applyCommonFilters(shipments, function (s) { return s.dateArrival || s.dateDeparture; }, { ...commonOpts, entityField: "supplierId", entityValue: entityValue });
    return buildShipmentStatusTotals(shp, invoices, slPassingIndex, lang);
  }, [loaded, reportType, shipments, dateFrom, dateTo, seasons, filterSeasons, entityValue, invoices, slPassingIndex, lang]);

  /* Stacked-status bar chart (2026-09-23, Arturo: "grafico de barras
     con los colores de cada status acumulados por semana... no con
     numero sino el color, mas color mayor numero" + "que me de la
     opcion de seleccionar si la grafica la quiero por semana o por mes
     o gasta por temporada"). Same filtered-shipments source as
     shipmentStatusTotals above, grouped by whichever granularity
     `chartPeriodBy` selects instead of collapsed into one grand total.
     Computed regardless of `includeChart` (cheap, and the checkbox
     only needs to gate whether it's PASSED to the PDF, not whether it
     exists) — see exportOpts below. */
  var shipmentWeeklyChart = useMemo(function () {
    /* 2026-09-23, Arturo: "si el Chart Period esta clear no debe
       incluir grafica" — clearing the Week/Month/Season selector
       (SearchSelect's own "Clear all") leaves chartPeriodBy falsy;
       without this guard buildShipmentPeriodStatusChart would still
       run and silently fall back to weekly grouping (chartPeriodOf's
       own default branch) while the selector visually showed nothing
       picked — never chart at all is the correct behavior instead. */
    if (!loaded || reportType !== "shipments" || !chartPeriodBy) return null;
    var commonOpts = { dateFrom: dateFrom, dateTo: dateTo, seasons: seasons, filterSeasons: filterSeasons };
    var shp = applyCommonFilters(shipments, function (s) { return s.dateArrival || s.dateDeparture; }, { ...commonOpts, entityField: "supplierId", entityValue: entityValue });
    return buildShipmentPeriodStatusChart(shp, invoices, slPassingIndex, lang, chartPeriodBy, seasons, seasonNameById);
  }, [loaded, reportType, shipments, dateFrom, dateTo, seasons, filterSeasons, entityValue, invoices, slPassingIndex, lang, chartPeriodBy, seasonNameById]);

  /* Arturo, 2026-09-17: "[lot/PO/date] deberian desaparecer solo si
     detail level es global (summary) porque si es detailed entonces
     si hay forma de detallar cada embarque" — column-dropping and
     row-collapsing when grouped both key off this same flag; see the
     matching isDetailedRecords inside previewRows above. */
  var isDetailedRecords = detailLevel === "detail" && colDef.hasDetailToggle;

  var exportColumns = useMemo(function () {
    var cols = activeColumns.filter(function (c) { return checkedCols[c.key]; }).map(function (c) { return { key: c.key, label: c.label[lang] || c.label.en, type: c.type, sum: c.sum }; });
    /* Arturo, 2026-09-17: "no es necesario tener una columna de cliente
       o shipper" — the entity-name column is redundant once its rows
       are wrapped in a section header (see previewRows/entityField
       above), so it's dropped from the visible/exported columns. */
    var entityField = ENTITY_FIELD_BY_REPORT[reportType];
    if (entityField) cols = cols.filter(function (c) { return c.key !== entityField; });
    if (groupBy !== "none") {
      /* Arturo, 2026-09-17: "si agrupo por semana, mes o temporada
         cualquier reporte la columna lot, PO, date debieran
         desaparecer" — groupRows() collapses many records into one
         row per period, and a non-sum column like Lot/PO#/Date/Status
         just keeps whichever value happened to be on the FIRST record
         folded into that period — meaningless (and misleading) once
         many different lots/dates/PUs are combined. Global/summary
         mode collapses, so those columns drop; Detailed mode never
         collapses (every record still gets its own row, see
         previewRows/isDetailedRecords), so they stay. */
      if (!isDetailedRecords) cols = cols.filter(function (c) { return c.sum || RECORD_IDENTIFIER_COLS.indexOf(c.key) === -1; });
      cols = [{ key: "period", label: t.groupBy, type: "text", sum: false }].concat(cols);
    }
    return cols;
  }, [activeColumns, checkedCols, groupBy, lang, t.groupBy, isDetailedRecords, reportType]);

  /* Arturo, 2026-09-17: "antes de generarlo debo poder ver la tabla de
     lo que seleccione... a manera de tabla dentro de la ventana...
     ya de ahi entonces genero a excel o pdf" — Execute renders the
     report on-screen from the CURRENT filters; Excel/PDF export from
     that executed snapshot, not from whatever the filters say right
     now. Any filter change after Execute invalidates the snapshot
     (previewRows gets a new reference) so Arturo can't export a table
     that no longer matches what's on screen. */
  var [executed, setExecuted] = useState(false);
  var [executedRows, setExecutedRows] = useState([]);
  useEffect(function () { setExecuted(false); }, [previewRows]);
  /* Arturo, 2026-09-17: "casillas de seleccion a la izquierda en caso
     de que existan renglones que no quiera mostrar... los totales se
     recalculan si oculto renglones" — a checkbox per row, checked by
     default; unchecking a row drops it from both the displayed table
     total AND the exported Excel/PDF (visibleRows, not executedRows).
     Resets to all-checked on every fresh Execute. */
  var [hiddenRows, setHiddenRows] = useState({});
  var handleExecute = function () { setExecutedRows(previewRows); setExecuted(true); setHiddenRows({}); };
  var toggleRowHidden = function (idx) {
    setHiddenRows(function (prev) {
      var next = { ...prev };
      if (next[idx]) delete next[idx]; else next[idx] = true;
      return next;
    });
  };
  var allHidden = executedRows.length > 0 && executedRows.every(function (r, i) { return hiddenRows[i]; });
  var toggleAllRows = function () {
    if (allHidden) { setHiddenRows({}); return; }
    var next = {}; executedRows.forEach(function (r, i) { next[i] = true; }); setHiddenRows(next);
  };
  /* Arturo, 2026-09-17: "en todos los reportes necesito poder ordenar
     todas las columnas como yo quiera" — click any header to sort the
     executed table by that column (toggles asc/desc on repeat clicks).
     Resets on every fresh Execute. Rows keep their original index
     (__idx) through the sort so the include/exclude checkboxes and
     hidden-row state still line up correctly no matter the display
     order. */
  var [sortCol, setSortCol] = useState(null);
  var [sortDir, setSortDir] = useState("asc");
  useEffect(function () { setSortCol(null); setSortDir("asc"); }, [executedRows]);
  var handleSortClick = function (key) {
    if (sortCol === key) { setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; }); }
    else { setSortCol(key); setSortDir("asc"); }
  };
  var sortedRows = useMemo(function () {
    var indexed = executedRows.map(function (r, i) { return { ...r, __idx: i }; });
    if (!sortCol) return indexed;
    var col = exportColumns.find(function (c) { return c.key === sortCol; });
    var isNum = col && (col.type === "money" || col.type === "number");
    indexed.sort(function (a, b) {
      var cmp;
      if (isNum) { cmp = (parseFloat(a[sortCol]) || 0) - (parseFloat(b[sortCol]) || 0); }
      else { cmp = String(a[sortCol] || "").localeCompare(String(b[sortCol] || "")); }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return indexed;
  }, [executedRows, sortCol, sortDir, exportColumns]);

  var visibleRows = useMemo(function () {
    return sortedRows.filter(function (r) { return !hiddenRows[r.__idx]; }).map(function (r) { var copy = { ...r }; delete copy.__idx; return copy; });
  }, [sortedRows, hiddenRows]);

  var grandTotal = useMemo(function () { return computeGrandTotal(visibleRows, exportColumns); }, [visibleRows, exportColumns]);

  var entityLabel = useMemo(function () {
    if (!entityValue) return t.allEntities;
    var opt = entityOptions.find(function (o) { return String(o.value) === String(entityValue); });
    return opt ? opt.label : entityValue;
  }, [entityValue, entityOptions, t.allEntities]);

  /* Arturo, 2026-09-23: "agregar a los headers la temporada, si
     selecciono mas de una no se agrega" — the Season filter never fed
     into periodLabel at all (single or multiple selected, same gap);
     every selected season's display name (buildSeasonFilterOptions
     already resolves seasonName/auto-generated name, same source the
     dropdown itself uses) now gets appended, comma-joined so multiple
     selections all show. Cleared filter ("Todas las temporadas" /
     empty array) adds nothing, same as before. seasonNameById itself
     is declared further up (before shipmentStatusTotals) so the chart
     memo can use it too. */
  var seasonNamesLabel = useMemo(function () {
    if (!filterSeasons || filterSeasons.length === 0) return "";
    return filterSeasons.map(function (id) { return seasonNameById[String(id)]; }).filter(Boolean).join(", ");
  }, [filterSeasons, seasonNameById]);

  var periodLabel = (dateFrom || dateTo) ? ((dateFrom || "…") + " – " + (dateTo || "…")) : (lang === "es" ? "Todo el historial" : "All history");
  if (seasonNamesLabel) periodLabel += " · " + seasonNamesLabel;

  /* Arturo, 2026-09-17: "aging no se muestra los headers como en xero,
     debe ser identico" (pasted Xero's own title block: report name /
     company legal name / "As of {date}" / "Aging by due date") — Aging
     gets this exact 4-line title instead of the generic one-liner
     every other report type uses. `asOfDate` is the filter's own "To"
     date when set (the aging snapshot's reference point), else today. */
  var agingAsOfDate = useMemo(function () {
    var d = dateTo ? new Date(dateTo + "T00:00:00") : new Date();
    return d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "long", day: "numeric" });
  }, [dateTo, lang]);

  /* Arturo, 2026-09-17: "el aging report de four seasons, el nombre
     que debe mostrar en el proveedor de ellos es sweetleaf no ground
     fresh, solo ese cliente" — Four Seasons only ever deals with GFI
     under the SweetLeaf brand (CLAUDE.md: "SweetLeaf is GFI's brand
     for sales to Four Seasons"), so the Receivables title block's
     "who this is from" line reads "Sweetleaf Produce Inc." instead of
     the real legal name whenever every client actually in scope is
     Four Seasons. Falls back to the real company name for every other
     client (and for Payables, which was never in question). */
  var agingCompanyName = useMemo(function () {
    if (reportType !== "aging" || entityType !== "client") return companyName;
    var names = entityValue ? [entityLabel] : [...new Set(previewRows.map(function (r) { return r.entityName; }).filter(Boolean))];
    return names.length > 0 && names.every(isFourSeasonsClientName) ? "Sweetleaf Produce Inc." : companyName;
  }, [reportType, entityType, entityValue, entityLabel, previewRows, companyName]);

  var exportOpts = function () {
    var opts = {
      reportTypeLabel: t[typeDef.labelKey],
      entityLabel: entityLabel,
      periodLabel: periodLabel,
      rows: visibleRows,
      columns: exportColumns,
      grandTotal: grandTotal,
      lang: lang,
      filenameBase: reportType,
      orientation: pdfOrientation,
    };
    if (reportType === "aging") {
      opts.agingKind = entityType === "client" ? "receivables" : "payables";
      opts.companyName = agingCompanyName;
      opts.asOfDate = agingAsOfDate;
    }
    /* 2026-09-23, Arturo: "agregale al reporte el nombre de
       ground-fresh" (Shipments) — deliberately scoped to just this
       report type, not every report, per the original 2026-09-17
       "no logo/branding" spec for the rest. */
    if (reportType === "shipments") {
      opts.companyName = companyName;
      /* 2026-09-23, Arturo: "totales de los distntos status tanto
         cajas como pallets con porcentaje... y por porducto" — fed
         into the PDF (its own cover page, buildStatusBreakdownHtml)
         and Excel export only, never rendered on screen (Arturo,
         same day: "no me lo muestras en la tabla, solo en la
         previsualizacion y en la impresion, me quita mucho espacio
         visible de la ventana" — the on-screen panel this used to
         feed was removed entirely, not just hidden). */
      if (shipmentStatusTotals && shipmentStatusTotals.rows.length > 0) opts.statusBreakdown = shipmentStatusTotals;
      /* 2026-09-23, Arturo: "grafico de barras con los colores de cada
         status acumulados por semana... esta barra agregarla a la
         primer hoja debajo del summary, si hay espacio debajo" — same
         PDF-only placement as statusBreakdown just above (never on
         screen, same reasoning), stacked onto that same first cover
         page below the breakdown tables rather than a page of its own. */
      /* 2026-09-23, Arturo: "incluso que me de la opcion si quiero
         incluir grafica o no" — includeChart gates it here (the data
         is always computed above, cheap either way) rather than
         skipping the useMemo, so toggling the checkbox never has to
         wait for a recompute. */
      if (includeChart && shipmentWeeklyChart && shipmentWeeklyChart.overall.length > 0) opts.weeklyChart = shipmentWeeklyChart;
    }
    return opts;
  };
  var handleGenerateExcel = function () {
    if (!executed || visibleRows.length === 0) return;
    exportReportXlsx(exportOpts());
  };
  /* Arturo, 2026-09-17: "al generar un reporte dame una
     previsualizacion antes de imprimir o descargarlo y ahi pueda
     cambiar y ver como se ve antes de hacerlo" — "Generate PDF" opens
     a real rendered-page preview (same iframe pattern as
     SalesReportPdfPreview) instead of downloading immediately; the
     Orientation control lives INSIDE that modal too, so changing it
     regenerates the preview live before Arturo ever downloads
     anything. */
  var [showPdfPreview, setShowPdfPreview] = useState(false);
  var handleGeneratePdf = function () {
    if (!executed || visibleRows.length === 0) return;
    setShowPdfPreview(true);
  };

  /* Send-to-shipper (2026-09-23, Arturo, Shipments report: "poder
     enviar ese esoe reportes via email al shipper") — only makes
     sense when a single, specific shipper is in scope (Entity =
     that shipper, not "All"), same as how the export buttons only
     act on an already-Executed snapshot. Always Ground-Fresh's
     masked account — the recipient here is always a shipper, never
     a client, so the Four Seasons/SweetLeaf routing rule
     (REGLA DE ENVÍO DE EMAILS) can never apply to this action, but
     the SweetLeaf account is still stripped from the picker for
     consistency with every other Ground-Fresh-only email flow. */
  var [showEmailModal, setShowEmailModal] = useState(false);
  var canEmailShipper = reportType === "shipments" && entityType === "supplier" && !!entityValue;
  var handleSendToShipper = async function () {
    if (!executed || visibleRows.length === 0 || !canEmailShipper) return;
    /* Same "force a fresh company_profile before opening any email
       dialog" fix already applied throughout the app (InvoiceRegistry.js/
       ShipmentRegistry.js) — loadCompanyProfile() only reads a
       synchronous in-memory cache that may be stale/empty in this tab. */
    try { await loadFromSupabase("company_profile"); } catch (e) {}
    setShowEmailModal(true);
  };

  /* Arturo, 2026-09-17: "ya vi porque no muestra los totales, ahi
     estan solo que no deja hacer scroll" — `.main-body` (App.css) is a
     FIXED-height, overflow:hidden shell on desktop by design (every
     other Registry screen relies on it, letting only ITS OWN table
     scroll while filters/buttons stay put) — this component's root
     div was plain block flow, so once the filters+columns+Execute
     sections plus the preview card together exceeded that fixed
     height, the tail end (including the Grand Total row) was clipped
     by .main-body itself, outside the reach of the inner table's own
     480px-tall scrollbar. Making the root a flex column that fills
     .main-body's height, with the preview card as the one flexible
     (not fixed-height) section, lets the table's own scroll area claim
     whatever room is actually available and reach every row. */
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ marginBottom: 14, flex: "0 0 auto" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
        <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14, padding: 14, border: "1px solid var(--color-border)", borderRadius: 10, background: "var(--color-bg-card)", flex: "0 0 auto" }}>
        <Field label={t.reportType} width={260}>
          <SearchSelect value={reportType} onChange={setReportType} options={REPORT_TYPE_OPTIONS.map(function (o) { return { value: o.value, label: t[o.labelKey] }; })} />
        </Field>

        {typeDef.entity === "either" && (
          <Field label={t.entityType} width={140}>
            <SearchSelect value={entityType} onChange={setEntityType} options={[{ value: "supplier", label: t.entitySupplier }, { value: "client", label: t.entityClient }]} />
          </Field>
        )}

        <Field label={typeDef.entity === "client" ? t.entityClient : typeDef.entity === "vendor" ? t.entitySupplier : t.entitySupplier} width={220}>
          <SearchSelect value={entityValue} onChange={setEntityValue} placeholder={t.allEntities} options={entityOptions} />
        </Field>

        <Field label={t.from} width={140}><input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} style={inp} /></Field>
        <Field label={t.to} width={140}><input type="date" value={dateTo} onChange={function (e) { setDateTo(e.target.value); }} style={inp} /></Field>

        <Field label={seasonFilterPlaceholder(lang)} width={200}>
          <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} />
        </Field>

        {reportType !== "aging" && (
          <Field label={t.groupBy} width={160}>
            <SearchSelect value={groupBy} onChange={setGroupBy} options={[
              { value: "none", label: t.groupNone }, { value: "week", label: t.groupWeek },
              { value: "month", label: t.groupMonth }, { value: "season", label: t.groupSeason },
            ]} />
          </Field>
        )}

        {colDef.hasStatusFilter && (
          <Field label={t.status} width={140}>
            <SearchSelect value={statusFilter} onChange={setStatusFilter} placeholder={t.statusAll} options={[{ value: "paid", label: t.statusPaid }, { value: "awaiting", label: t.statusPending }, { value: "draft", label: t.statusDraft }, { value: "overdue", label: t.statusOverdue }]} />
          </Field>
        )}

        {colDef.hasDetailToggle && (
          <Field label={t.detailLevel} width={160}>
            <SearchSelect value={detailLevel} onChange={setDetailLevel} options={[{ value: "detail", label: t.detailDetailed }, { value: "global", label: t.detailGlobal }]} />
          </Field>
        )}

        <Field label={t.pdfOrientation} width={150}>
          <SearchSelect value={pdfOrientation} onChange={setPdfOrientation} options={[{ value: "auto", label: t.orientationAuto }, { value: "portrait", label: t.orientationPortrait }, { value: "landscape", label: t.orientationLandscape }]} />
        </Field>

        {/* Weekly/monthly/seasonal status chart controls (2026-09-23,
            Arturo: "que me de la opcion de seleccionar si la grafica la
            quiero por semana o por mes o gasta por temporada si quiero
            cpomparar varias temporadas" + "incluso que me de la opcion
            si quiero incluir grafica o no"). Shipments report only —
            the chart itself only exists there. */}
        {reportType === "shipments" && (
          <>
            <Field label={t.chartPeriod} width={140}>
              <SearchSelect value={chartPeriodBy} onChange={setChartPeriodBy} disabled={!includeChart} options={[{ value: "week", label: t.chartPeriodWeek }, { value: "month", label: t.chartPeriodMonth }, { value: "season", label: t.chartPeriodSeason }]} />
            </Field>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={includeChart} onChange={function (e) { setIncludeChart(e.target.checked); }} />
                {t.includeChart}
              </label>
            </div>
          </>
        )}
      </div>

      <div style={{ marginBottom: 14, padding: 14, border: "1px solid var(--color-border)", borderRadius: 10, background: "var(--color-bg-card)", flex: "0 0 auto" }}>
        <span style={fieldLabel}>{t.columns}</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
          {activeColumns.filter(function (c) { return c.key !== ENTITY_FIELD_BY_REPORT[reportType]; }).map(function (c) {
            return (
              <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={!!checkedCols[c.key]} onChange={function (e) { setCheckedCols(function (prev) { return { ...prev, [c.key]: e.target.checked }; }); }} />
                {c.label[lang] || c.label.en}
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flex: "0 0 auto" }}>
        <button onClick={handleExecute} disabled={!loaded || previewRows.length === 0} style={{ padding: "10px 20px", borderRadius: 7, border: "none", background: (!loaded || previewRows.length === 0) ? "#9ca3af" : "var(--color-accent)", color: "#fff", cursor: (!loaded || previewRows.length === 0) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
          {"▶ "}{t.execute}
        </button>
        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          {!loaded ? t.noDataYet : previewRows.length === 0 ? t.noRows : previewRows.length.toLocaleString() + " " + t.rowsReady}
        </span>
      </div>

      {executed && executedRows.length > 0 && (
        <div style={{ marginBottom: 14, padding: 14, border: "1px solid var(--color-border)", borderRadius: 10, background: "var(--color-bg-card)", flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          {reportType === "aging" && (
            <div style={{ marginBottom: 10, flex: "0 0 auto" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-accent)" }}>{entityType === "client" ? (lang === "es" ? "Detalle de Antigüedad de Cuentas por Cobrar" : "Accounts Receivable Aging Detail") : (lang === "es" ? "Detalle de Antigüedad de Cuentas por Pagar" : "Accounts Payable Aging Detail")}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 2 }}>{agingCompanyName}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{(lang === "es" ? "Al " : "As of ") + agingAsOfDate}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{lang === "es" ? "Antigüedad por fecha de vencimiento" : "Aging by due date"}</div>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flex: "0 0 auto" }}>
            <span style={fieldLabel}>{t.previewTitle}</span>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleGenerateExcel} disabled={visibleRows.length === 0} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: visibleRows.length === 0 ? "#9ca3af" : "var(--color-accent)", color: "#fff", cursor: visibleRows.length === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700 }}>
                {"⬇ "}{t.generate}
              </button>
              <button onClick={handleGeneratePdf} disabled={visibleRows.length === 0} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: visibleRows.length === 0 ? "#9ca3af" : "var(--color-accent)", cursor: visibleRows.length === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700 }}>
                {"👁 "}{t.previewPdf}
              </button>
              {canEmailShipper && (
                <button onClick={handleSendToShipper} disabled={visibleRows.length === 0} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: visibleRows.length === 0 ? "#9ca3af" : "var(--color-accent)", cursor: visibleRows.length === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700 }}>
                  {"✉ "}{t.sendToShipper}
                </button>
              )}
            </div>
          </div>
          <div style={{ overflowX: "auto", overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
              <thead>
                <tr>
                  <th style={{ position: "sticky", top: 0, background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)", padding: "6px 8px", width: 28 }}>
                    <input type="checkbox" checked={!allHidden} onChange={toggleAllRows} title={t.includeRow} />
                  </th>
                  {exportColumns.map(function (c) {
                    var isNum = c.type === "money" || c.type === "number";
                    var isSorted = sortCol === c.key;
                    return (
                      <th key={c.key} onClick={function () { handleSortClick(c.key); }} title={t.sortColumn}
                        style={{ position: "sticky", top: 0, background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)", padding: "6px 8px", textAlign: isNum ? "right" : "left", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.03em", color: isSorted ? "var(--color-accent)" : "var(--color-text-secondary)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                        {c.label}{isSorted ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(function (r) {
                  var i = r.__idx;
                  /* Arturo, 2026-09-17: entity section headers — a full-
                     width bold row with just the entity name, always
                     included (no checkbox — hiding a shipper's header
                     while keeping its rows would be confusing), never
                     dimmed. */
                  if (r._entityHeader) {
                    return (
                      <tr key={i}>
                        <td colSpan={exportColumns.length + 1} style={{ padding: "8px 8px 4px", fontWeight: 700, fontSize: 12.5, color: "var(--color-accent)", borderBottom: "2px solid var(--color-accent)" }}>{r.entityLabel}</td>
                      </tr>
                    );
                  }
                  var isSub = !!r._subtotal;
                  var isHidden = !!hiddenRows[i];
                  return (
                    <tr key={i} style={{ opacity: isHidden ? 0.4 : 1, ...(isSub ? { background: "var(--color-bg-hover)", fontWeight: 700, borderTop: "1px solid var(--color-border)", borderBottom: "1px solid var(--color-border)" } : {}) }}>
                      <td style={{ padding: "5px 8px", borderBottom: "1px solid var(--color-border)" }}>
                        <input type="checkbox" checked={!isHidden} onChange={function () { toggleRowHidden(i); }} title={t.includeRow} />
                      </td>
                      {exportColumns.map(function (c) {
                        var isNum = c.type === "money" || c.type === "number";
                        var v = r[c.key];
                        /* Status column — colored badge instead of plain
                           text, same palette as ShipmentRegistry.js's own
                           SALE column (STATUS_COLORS, reportGeneratorCalc.js).
                           2026-09-23, Arturo: "bien pero muestra los
                           colores en el status tambien". */
                        if (c.key === "status" && v) {
                          var sc = STATUS_COLORS[r.statusKey];
                          return <td key={c.key} style={{ padding: "5px 8px", borderBottom: "1px solid var(--color-border)", whiteSpace: "nowrap" }}>
                            {sc
                              ? <span style={{ padding: "2px 8px", borderRadius: 8, fontSize: 10.5, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border }}>{v}</span>
                              : v}
                          </td>;
                        }
                        var display = v === undefined || v === null || v === "" ? "" : (c.type === "money" ? fmtMoneyCell(v) : c.type === "number" ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 }) : v);
                        return <td key={c.key} style={{ padding: "5px 8px", borderBottom: "1px solid var(--color-border)", textAlign: isNum ? "right" : "left", fontFamily: isNum ? '"Courier New",monospace' : "inherit", whiteSpace: "nowrap" }}>{display}</td>;
                      })}
                    </tr>
                  );
                })}
                {grandTotal && Object.keys(grandTotal).length > 0 && (
                  <tr style={{ fontWeight: 700, borderTop: "2px solid var(--color-accent)" }}>
                    <td style={{ padding: "6px 8px" }} />
                    {exportColumns.map(function (c, i) {
                      var isNum = c.type === "money" || c.type === "number";
                      if (isNum) {
                        var v = grandTotal[c.key];
                        var totalDisplay = v === undefined ? "" : (c.type === "money" ? fmtMoneyCell(v) : Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 }));
                        return <td key={c.key} style={{ padding: "6px 8px", textAlign: "right", fontFamily: '"Courier New",monospace' }}>{totalDisplay}</td>;
                      }
                      return <td key={c.key} style={{ padding: "6px 8px" }}>{i === 0 ? t.grandTotalLabel : ""}</td>;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!executed && loaded && previewRows.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.notExecutedYet}</div>
      )}
      {showPdfPreview && (
        <ReportPdfPreviewModal
          baseOpts={exportOpts()}
          lang={lang}
          t={t}
          onClose={function () { setShowPdfPreview(false); }}
        />
      )}
      {showEmailModal && canEmailShipper && (
        <EmailShipmentsReportModal
          opts={exportOpts()}
          supplier={suppliers.find(function (s) { return String(s.id) === String(entityValue); })}
          entityLabel={entityLabel}
          periodLabel={periodLabel}
          lang={lang}
          t={t}
          onClose={function () { setShowEmailModal(false); }}
        />
      )}
    </div>
  );
}

/* Arturo, 2026-09-17: "al generar un reporte dame una previsualizacion
   antes de imprimir o descargarlo y ahi pueda cambiar y ver como se ve
   antes de hacerlo" — same iframe-preview pattern already used by
   SalesReports.js's SalesReportPdfPreview. The Orientation control
   lives here (not just in the filter bar) so changing it regenerates
   the preview live, before anything is downloaded. */
function ReportPdfPreviewModal({ baseOpts, lang, t, onClose, zIndex }) {
  var [orientation, setOrientation] = useState(baseOpts.orientation || "portrait");
  var [blob, setBlob] = useState(null);
  var [filename, setFilename] = useState("");
  var [blobUrl, setBlobUrl] = useState(null);
  var [error, setError] = useState(null);

  useEffect(function () {
    var cancelled = false;
    var createdUrl = null;
    setBlob(null); setBlobUrl(null); setError(null);
    buildReportPdfBlob({ ...baseOpts, orientation: orientation }).then(function (built) {
      if (cancelled) return;
      createdUrl = URL.createObjectURL(built.blob);
      setBlob(built.blob); setFilename(built.filename); setBlobUrl(createdUrl);
    }).catch(function (e) {
      if (!cancelled) setError(e && e.message ? e.message : String(e));
    });
    return function () { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation]);

  var handleDownload = function () {
    if (!blob) return;
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url; a.download = filename || "GFI_Report.pdf";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  var btn = { padding: "6px 16px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 };
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: zIndex || 10002, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: "90vw", height: "90vh", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{t.pdfPreviewTitle}</span>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase" }}>{t.pdfOrientation}</span>
            <div style={{ width: 140 }}>
              <SearchSelect value={orientation} onChange={setOrientation} options={[{ value: "auto", label: t.orientationAuto }, { value: "portrait", label: t.orientationPortrait }, { value: "landscape", label: t.orientationLandscape }]} />
            </div>
            <button onClick={handleDownload} disabled={!blob} style={{ ...btn, border: "none", background: blob ? "#1B3A4B" : "var(--color-border)", color: blob ? "#fff" : "var(--color-text-muted)", cursor: blob ? "pointer" : "not-allowed" }}>{t.download}</button>
            <button onClick={onClose} style={{ ...btn, border: "1px solid var(--color-border)", background: "transparent", color: "#555" }}>{t.close}</button>
          </div>
        </div>
        <div style={{ flex: 1, background: "#525659", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {error && <div style={{ color: "#fff", fontSize: 13, padding: 20, textAlign: "center" }}>{(lang === "es" ? "Error al generar el PDF: " : "Could not generate PDF: ") + error}</div>}
          {!error && !blobUrl && <div style={{ color: "#fff", fontSize: 13 }}>{lang === "es" ? "Generando PDF…" : "Generating PDF…"}</div>}
          {blobUrl && <iframe title="report-pdf-preview" src={blobUrl} style={{ width: "100%", height: "100%", border: "none" }} />}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* Send-to-shipper (2026-09-23, Arturo, Shipments report). Thin
   wrapper around the shared EmailDialog — same pattern as
   SalesReports.js's EmailSalesReportModal. Attaches the same PDF
   `buildReportPdfBlob` already builds for the preview/download
   buttons (so the emailed file is always identical to what Arturo
   last saw), addressed to the selected shipper's contacts. Always
   sent from Ground-Fresh's masked account — the recipient here is
   always a shipper, never a client, so REGLA DE ENVÍO DE EMAILS'
   Four Seasons/SweetLeaf block doesn't apply, but the SweetLeaf
   account is still stripped from the picker for consistency with
   every other Ground-Fresh-only email flow in this app. */
function EmailShipmentsReportModal({ opts, supplier, entityLabel, periodLabel, lang, t, onClose }) {
  /* 2026-10-07, Arturo: "quiero agregar una previsualizacion del pdf
     como en todos los modulos, ahora solo veo el preview del email" —
     same pattern as the Quality Report/Protection Report preview
     already built into the "Protection Status Update" email dialog
     in InvoiceRegistry.js: a topContent row above the message body
     with a "Vista previa" button that opens the exact PDF
     (ReportPdfPreviewModal, the same component the standalone
     "👁 Preview PDF" button already uses) this dialog will attach. */
  var [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  var company = useMemo(function () { return loadCompanyProfile(); }, []);
  /* 2026-09-30 — also excludes "automat" (GFI Automated / SL
     Automated) now, same fix applied everywhere else this filter is
     duplicated; it only ever excluded "sweetleaf" in the name. */
  var gfOnlyAccounts = useMemo(function () { return (company.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; }); }, [company]);
  var shortName = (supplier && (supplier.shortName || supplier.fullName || supplier.name)) || entityLabel || "Shipper";
  var today = new Date().toISOString().substring(0, 10);
  var subject = "Shipments_Report_" + String(shortName).replace(/[\\/:*?"<>|]/g, "").trim() + "_" + today;
  var filename = subject + ".pdf";

  /* Same category as the other business-report doc sent to shippers
     (SalesReports.js's Sales Report) — this is conceptually the same
     kind of communication, no need for a brand-new opt-in category. */
  var recipients = useMemo(function () {
    var contacts = filterContactsByCategory(supplier && supplier.contacts, "sales_report");
    var list = contacts.length > 0
      ? contacts.map(function (c) { return { name: c.name || "", email: c.email }; })
      : (supplier && supplier.email ? [{ name: supplier.shortName || supplier.fullName || "", email: supplier.email }] : []);
    return list.map(function (r) { return { name: r.name, email: r.email, checked: true }; });
  }, [supplier]);

  var buildAttachments = async function () {
    var built = await buildReportPdfBlob(opts);
    return [{ filename: filename, blob: built.blob, mimeType: "application/pdf" }];
  };

  return (
    <>
    <EmailDialog
      lang={lang}
      title={t.sendReportTitle + " — " + entityLabel + " · " + periodLabel}
      recipients={recipients}
      docOptions={[]}
      defaultSubject={subject}
      defaultBody={t.sendReportBody}
      fromEmail={(gfOnlyAccounts.find(function (a) { return a.maskedFrom; }) || {}).maskedFrom || company.outboundEmail || ""}
      emailAccounts={gfOnlyAccounts}
      signatures={company.emailSignatures || []}
      topContent={(
        <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
          <span style={{ fontSize: 11, fontWeight: 700 }}>{"📄 "}{filename}</span>
          <button type="button" onClick={function () { setPdfPreviewOpen(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
            {lang === "es" ? "Vista previa" : "Preview"}
          </button>
        </div>
      )}
      alwaysBuildAttachments={true}
      buildAttachments={buildAttachments}
      postProcessHtmlBody={function (html, resolvedFrom) { return appendDisclosureByAccount(html, resolvedFrom, company); }}
      onClose={onClose}
      zIndex={10003}
    />
    {pdfPreviewOpen && (
      <ReportPdfPreviewModal
        baseOpts={opts}
        lang={lang}
        t={t}
        onClose={function () { setPdfPreviewOpen(false); }}
        zIndex={10004}
      />
    )}
    </>
  );
}
